import { dedup, isNil } from '@gfe/core/utils/shared-utils';
import { CalendarEvent } from '@gfe/features/components/visualization/calendar/models/calendar.models';
import { VisuItemLabel } from '@gfe/features/components/visualization/common/models/label.model';
import {
  GaliaMarker,
  MarkerType
} from '@gfe/features/components/visualization/mapbox/models/galia-marker';
import { GaliaRoute } from '@gfe/features/components/visualization/mapbox/models/galia-route';

import { VisualizationModule } from '@core/models/data-visualization/data-visualization.model';
import {
  AuxiliaryDataBundle,
  EntityInterface,
  TableColCfg
} from '@core/models/entities/entity-visualization-interface';
import { VisualizationBuilders } from '@core/models/entities/generic-entity';
import { EntityCfgOverrides } from '@core/models/entities/generic-entity-cfg';
import {
  QuantifiedExpertise,
  Quantity
} from '@core/models/entities/implementations/quantified-expertise';
import { DateTimeWindow } from '@core/models/entities/implementations/time-window';
import { GenericEntityRegistryService } from '@core/services/user-models-registry/generic-entity-registry.service';

import { stringToColor } from '../../colors.model';
import { RawKpi } from '../../kpi.model';
import { Coords } from './coords';
import { CustomData } from './custom-data';
import {
  SiteSpecification,
  getOperationSite,
  getSpecificSiteUid,
  siteSpecificationCfgOverrides
} from './user-job-transport-order-v1';
import { UserSiteV1Model } from './user-site-v1';

/**
 * The elementary bin manoeuvre performed by a step, mirroring the data-layer
 * `WasteContainerJobKind` (wire form is snake_case).
 */
export type WasteContainerJobKind =
  | 'drop_empty'
  | 'take_full'
  | 'take_empty'
  | 'load'
  | 'unload'
  | 'service';

export interface WasteContainerStep {
  uid?: string;
  kind: WasteContainerJobKind;
  site: SiteSpecification;
  duration: string; // in ISO8601
  datetime_windows?: DateTimeWindow[];
  job_can_be_preempted_after: boolean;
}

export interface UserWasteContainerJobV1Model {
  uid: string;
  name?: string;
  steps: WasteContainerStep[];
  vehicle_capacity_requirement: Quantity[];
  transported_commodity_uid?: string;
  compatible_bin_uids: string[];
  equipment_requirement_uids?: string[];
  expertise_requirements: QuantifiedExpertise[];
  externalization_cost?: number;
  kpis: RawKpi[];
  custom_data: CustomData;
}

export type UserWasteContainerJobV1InputModel = Omit<UserWasteContainerJobV1Model, 'uid'>;

/** Turns a snake_case kind into a readable label, e.g. `drop_empty` -> `drop empty`. */
export function formatWasteContainerJobKind(kind: WasteContainerJobKind): string {
  return kind.replace(/_/g, ' ');
}

function indexSitesByUid(auxData?: AuxiliaryDataBundle): Record<string, UserSiteV1Model> {
  const sites = (auxData?.entitiesArrayPerVersionName['UserSite'] ??
    []) as UserSiteV1Model[];
  return Object.fromEntries(sites.map((site) => [site.uid, site]));
}

function resolveStepCoords(
  step: WasteContainerStep,
  auxiliarySites: Record<string, UserSiteV1Model>
): Coords | undefined {
  const siteUid = getSpecificSiteUid(step.site);
  if (siteUid === undefined) return undefined;
  return auxiliarySites[siteUid]?.coords;
}

function createStepLabels(jobUid: string, step: WasteContainerStep): VisuItemLabel[] {
  return [
    { label: 'site', level: 0, value: getOperationSite(step.site) },
    { label: 'kind', level: 0, value: formatWasteContainerJobKind(step.kind) },
    { label: 'job', level: 0, value: jobUid }
  ];
}

const coordKey = (c: Coords): string => `${c.lat},${c.lng}`;

/**
 * Removes consecutive duplicate points so the chained route never contains
 * zero-length segments when two adjacent steps share the same site.
 */
export function chainStepCoords(coords: Coords[]): Coords[] {
  const chained: Coords[] = [];
  for (const c of coords) {
    const prev = chained[chained.length - 1];
    if (prev && coordKey(prev) === coordKey(c)) continue;
    chained.push(c);
  }
  return chained;
}

export const wasteContainerJobCfgOverrides: EntityCfgOverrides<UserWasteContainerJobV1Model> =
  {
    // The deployed backend schema does not yet declare x-visualizations for this
    // entity, so surface the map/calendar tabs from the front end. This mirrors the
    // behaviour of UserJobTransportOrder, whose data-layer macro declares them.
    customVisualizations: new Set<VisualizationModule>(['map', 'calendar']),
    // Add a read-only column listing every distinct site touched by the job's steps.
    // `steps` is an x-detail-entities path, so the generic generator skips it and never
    // emits a site column of its own (issue #1415). `autoColumns` already carries the
    // schema-driven columns — including the x-split-columns for vehicle capacity
    // requirements (issue #1433) — so we preserve them and only insert the sites list.
    buildColumns: (
      autoColumns: TableColCfg<EntityInterface<UserWasteContainerJobV1Model>>[]
    ): TableColCfg<EntityInterface<UserWasteContainerJobV1Model>>[] => {
      const sitesColumn: TableColCfg<EntityInterface<UserWasteContainerJobV1Model>> = {
        key: 'raw.steps.site' as keyof EntityInterface<UserWasteContainerJobV1Model>,
        label: 'table.columns.sites',
        type: 'uid-list',
        // Tag the list as a SITE reference so the uid/name toggle, popover and
        // name resolution light up (the same treatment the detail-table step
        // `site` column gets). This column is computed from `steps` and carries no
        // filterCfg, so the reference type is declared directly on the column.
        referenceEntityType: 'SITE',
        rawValue(row?: EntityInterface<UserWasteContainerJobV1Model>): unknown {
          const job = row?.raw;
          if (isNil(job)) return undefined;
          return dedup(job.steps.map((s) => getOperationSite(s.site)));
        },
        renderHook(_value, row) {
          const job = row?.raw;
          if (isNil(job)) return undefined;
          return dedup(job.steps.map((s) => getOperationSite(s.site)));
        }
      };

      const cols = [...autoColumns];
      const nameIdx = cols.findIndex((c) => (c.key as string) === 'raw.name');
      const insertIdx = nameIdx >= 0 ? nameIdx + 1 : cols.length;
      cols.splice(insertIdx, 0, sitesColumn);
      return cols;
    }
  };

export const wasteContainerStepCfgOverrides =
  siteSpecificationCfgOverrides<WasteContainerStep>();

export const wasteContainerJobVisualizationBuilders: VisualizationBuilders<UserWasteContainerJobV1Model> =
  {
    buildMarkers: (
      raw: UserWasteContainerJobV1Model,
      auxData?: AuxiliaryDataBundle
    ): GaliaMarker[] => {
      const auxiliarySites = indexSitesByUid(auxData);
      const color = stringToColor(raw.uid);
      const markers: GaliaMarker[] = [];

      raw.steps.forEach((step, i) => {
        const coords = resolveStepCoords(step, auxiliarySites);
        if (!coords) return;
        const window = step.datetime_windows?.[0];
        markers.push({
          uid: `${raw.uid}_step_${i}`,
          type: 'basic' as MarkerType,
          title: getOperationSite(step.site),
          coords,
          color,
          description: {
            name: raw.uid,
            type: formatWasteContainerJobKind(step.kind),
            startDate: window ? new Date(window.start) : undefined,
            endDate: window ? new Date(window.finish) : undefined,
            content: createStepLabels(raw.uid, step)
          },
          structuralAggregatorIds: [raw.uid]
        });
      });

      return markers;
    },

    buildRoutes: (
      raw: UserWasteContainerJobV1Model,
      _markers,
      auxData?: AuxiliaryDataBundle
    ): GaliaRoute[] => {
      const auxiliarySites = indexSitesByUid(auxData);

      const stepCoords = raw.steps
        .map((step) => resolveStepCoords(step, auxiliarySites))
        .filter((c): c is Coords => !isNil(c));

      const polyline = chainStepCoords(stepCoords);
      if (polyline.length < 2) return [];

      return [
        {
          aggregatorUid: raw.uid,
          color: stringToColor(raw.uid),
          coords: polyline,
          name: raw.uid,
          uid: `${raw.uid}_route`,
          width: 5
        }
      ];
    },

    buildCalendarEvents: (raw: UserWasteContainerJobV1Model): CalendarEvent[] => {
      const color = stringToColor(raw.uid);
      const events: CalendarEvent[] = [];

      raw.steps.forEach((step, stepIndex) => {
        (step.datetime_windows ?? []).forEach((window, i) => {
          events.push({
            uid: `${raw.uid}_step_${stepIndex}_${i}`,
            title: `${raw.uid} - ${formatWasteContainerJobKind(step.kind)}`,
            start: new Date(window.start),
            end: new Date(window.finish),
            mainColor: color,
            labels: []
          });
        });
      });

      return events;
    }
  };

export function registerWasteContainerJobExtensions(
  registry: GenericEntityRegistryService
): void {
  registry.registerOverrides('UserWasteContainerJob', wasteContainerJobCfgOverrides);
  registry.registerOverrides('UserJobWasteContainerStep', wasteContainerStepCfgOverrides);
  registry.registerVisualizationBuilders(
    'UserWasteContainerJob',
    wasteContainerJobVisualizationBuilders
  );
}
